import { observe } from "jotai-effect";
import {
    BookviewConversationAtom,
    ConversationAtom,
    ModalConversationAtom,
    NotebookConversationAtom,
} from "./current-conversation/conversation-atoms";
import {
    ConversationStateStatus,
    CurrentConversationState,
} from "./current-conversation/conversation-atom.reducer";
import { loadConversation } from "./current-conversation/load-conversation.thunk";

type ConversationWindowKey = "modal" | "bookview" | "notebook";

const CONVERSATION_WINDOW_ATOMS: Record<
    ConversationWindowKey,
    ConversationAtom
> = {
    modal: ModalConversationAtom,
    bookview: BookviewConversationAtom,
    notebook: NotebookConversationAtom,
};

const CONVERSATION_WINDOW_KEYS = Object.keys(
    CONVERSATION_WINDOW_ATOMS
) as Array<ConversationWindowKey>;

interface WindowSyncState {
    lastSyncedConversationId?: string;
    lastSyncedMessageCount: number;
    loadingConversationId?: string;
}

const windowSyncState: Record<ConversationWindowKey, WindowSyncState> = {
    modal: { lastSyncedMessageCount: 0 },
    bookview: { lastSyncedMessageCount: 0 },
    notebook: { lastSyncedMessageCount: 0 },
};

interface ConversationState {
    conversationId: string | undefined;
    messageCount: number;
    status: ConversationStateStatus;
}

type ConversationStateByWindow = Record<
    ConversationWindowKey,
    ConversationState
>;

function extractConversationState(
    state: CurrentConversationState
): ConversationState {
    return {
        conversationId: state.currentConversation?.id,
        messageCount: state.currentConversation?.messages.length ?? 0,
        status: state.status,
    };
}

/**
 * When a window's triggered load finishes, mark every window currently showing
 * that same conversation as synced so they don't immediately re-trigger each other.
 * Returns true if any window's load just completed (skip triggering new syncs this pass).
 */
function handleLoadingCompletions(
    windowStates: ConversationStateByWindow
): boolean {
    let handledCompletion = false;

    for (const key of CONVERSATION_WINDOW_KEYS) {
        const sync = windowSyncState[key];
        const state = windowStates[key];

        if (
            sync.loadingConversationId &&
            sync.loadingConversationId === state.conversationId &&
            state.status === ConversationStateStatus.ConversationReady
        ) {
            const conversationId = state.conversationId as string;

            for (const otherKey of CONVERSATION_WINDOW_KEYS) {
                if (windowStates[otherKey].conversationId === conversationId) {
                    windowSyncState[otherKey].lastSyncedConversationId =
                        conversationId;
                    windowSyncState[otherKey].lastSyncedMessageCount =
                        windowStates[otherKey].messageCount;
                }
            }

            sync.loadingConversationId = undefined;
            handledCompletion = true;
        }
    }

    return handledCompletion;
}

/**
 * For every conversation shared by 2+ windows, propagate fresh messages from
 * whichever window(s) have unsynced content to whichever window(s) are behind.
 */
function triggerSyncsForSharedConversations(
    windowStates: ConversationStateByWindow,
    set: Parameters<Parameters<typeof observe>[0]>[1]
): void {
    const conversationIds = Array.from(
        new Set(
            CONVERSATION_WINDOW_KEYS.map(
                (key) => windowStates[key].conversationId
            ).filter((id): id is string => !!id)
        )
    );

    for (const conversationId of conversationIds) {
        const keysInGroup = CONVERSATION_WINDOW_KEYS.filter(
            (key) => windowStates[key].conversationId === conversationId
        );

        if (keysInGroup.length < 2) {
            continue;
        }

        for (const sourceKey of keysInGroup) {
            const source = windowStates[sourceKey];
            const sourceSync = windowSyncState[sourceKey];

            if (
                source.status !== ConversationStateStatus.ConversationReady ||
                source.messageCount <= sourceSync.lastSyncedMessageCount
            ) {
                continue;
            }

            for (const targetKey of keysInGroup) {
                if (targetKey === sourceKey) {
                    continue;
                }

                const target = windowStates[targetKey];
                const targetSync = windowSyncState[targetKey];

                if (
                    targetSync.loadingConversationId ||
                    target.status ===
                        ConversationStateStatus.ConversationLoading
                ) {
                    continue;
                }

                const targetNeedsSync =
                    target.messageCount < source.messageCount ||
                    targetSync.lastSyncedConversationId !== conversationId;

                if (targetNeedsSync) {
                    targetSync.loadingConversationId = conversationId;
                    set(
                        CONVERSATION_WINDOW_ATOMS[targetKey],
                        async (dispatch) => {
                            await loadConversation(conversationId)(dispatch);
                        }
                    );
                }
            }
        }
    }
}

/**
 * Resets tracking for any window whose current conversation differs from what
 * was last synced, so a fresh conversation (e.g. loaded via history) starts clean.
 */
function resetTrackingForChangedConversations(
    windowStates: ConversationStateByWindow
): void {
    for (const key of CONVERSATION_WINDOW_KEYS) {
        const state = windowStates[key];
        const sync = windowSyncState[key];

        if (state.conversationId !== sync.lastSyncedConversationId) {
            sync.lastSyncedConversationId = state.conversationId;
            sync.lastSyncedMessageCount = state.messageCount;
            sync.loadingConversationId = undefined;
        }
    }
}

export function conversationsAtomSyncEffect() {
    return observe((get, set) => {
        const windowStates = CONVERSATION_WINDOW_KEYS.reduce((acc, key) => {
            acc[key] = extractConversationState(
                get(CONVERSATION_WINDOW_ATOMS[key])
            );
            return acc;
        }, {} as ConversationStateByWindow);

        if (handleLoadingCompletions(windowStates)) {
            return;
        }

        triggerSyncsForSharedConversations(windowStates, set);
        resetTrackingForChangedConversations(windowStates);
    });
}
